/**
 * Mock API Service for Demo Application
 *
 * This service simulates all backend API calls using local mock data.
 * It maintains state in localStorage and provides the same interface as the real API.
 */

import {
  mockUsers,
  mockManuscripts,
  mockBetaApplications,
  mockQueries,
  mockFeedback,
  MockUser,
  MockManuscript,
  MockBetaApplication,
  MockQuery,
  MockFeedback,
  getUserByEmail,
  getManuscriptsByWriter,
  getApplicationsByReader,
  getApplicationsByManuscript,
  getQueriesByWriter,
  getQueriesByAgent,
  getFeedbackByManuscript,
  getApprovedReaders,
} from './mockData';

// Initialize mock data in localStorage if not already present
const STORAGE_KEY = 'manuscriptMatchDemo';
const STORAGE_VERSION = 5; // Increment when data structure changes

interface DemoStorage {
  version: number;
  users: MockUser[];
  manuscripts: MockManuscript[];
  betaApplications: MockBetaApplication[];
  queries: MockQuery[];
  feedback: MockFeedback[];
  currentUser: MockUser | null;
}

const initializeStorage = () => {
  const existing = localStorage.getItem(STORAGE_KEY);
  let shouldReinitialize = false;
  let currentUser = null;

  if (existing) {
    try {
      const data = JSON.parse(existing);
      // Save current user before reinitializing
      currentUser = data.currentUser;
      // Reinitialize if version is outdated or missing
      if (!data.version || data.version < STORAGE_VERSION) {
        console.log(`[MockAPI] Upgrading storage from version ${data.version || 0} to ${STORAGE_VERSION}`);
        shouldReinitialize = true;
      }
    } catch {
      console.log('[MockAPI] Failed to parse existing storage, reinitializing');
      shouldReinitialize = true;
    }
  } else {
    console.log('[MockAPI] No existing storage, initializing');
    shouldReinitialize = true;
  }

  if (shouldReinitialize) {
    const initialData: DemoStorage = {
      version: STORAGE_VERSION,
      users: mockUsers,
      manuscripts: mockManuscripts,
      betaApplications: mockBetaApplications,
      queries: mockQueries,
      feedback: mockFeedback,
      currentUser: currentUser, // Preserve current user session
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(initialData));
    console.log('[MockAPI] Storage initialized with version', STORAGE_VERSION);
  }
};

const getStorage = (): DemoStorage => {
  const data = localStorage.getItem(STORAGE_KEY);
  return data ? JSON.parse(data) : {
    version: STORAGE_VERSION,
    users: mockUsers,
    manuscripts: mockManuscripts,
    betaApplications: mockBetaApplications,
    queries: mockQueries,
    feedback: mockFeedback,
    currentUser: null,
  };
};

const setStorage = (data: DemoStorage) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// Simulate network delay
const delay = (ms: number = 300) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to format user data for API responses
const formatUserForResponse = (user: MockUser | undefined) => {
  if (!user) return undefined;
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    displayName: user.name, // Add displayName for compatibility
    role: user.role,
  };
};

// Mock API class
export class MockAPI {
  // Auth endpoints
  static async login(email: string, password: string) {
    await delay();
    const storage = getStorage();
    const user = storage.users.find(u => u.email === email && u.password === password);

    if (!user) {
      throw {
        status: 401,
        message: 'Invalid email or password',
      };
    }

    if (!user.verified) {
      throw {
        status: 403,
        message: 'Please verify your email before logging in',
      };
    }

    storage.currentUser = user;
    setStorage(storage);

    // Simulate JWT token
    const token = `demo-token-${user.id}`;
    localStorage.setItem('token', token);

    return {
      token,
      user: {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        verified: user.verified,
      },
    };
  }

  static async logout() {
    await delay();
    const storage = getStorage();
    storage.currentUser = null;
    setStorage(storage);
    localStorage.removeItem('token');
    return { message: 'Logged out successfully' };
  }

  static async me() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw {
        status: 401,
        message: 'Not authenticated',
      };
    }

    return {
      user: {
        id: storage.currentUser.id,
        email: storage.currentUser.email,
        name: storage.currentUser.name,
        role: storage.currentUser.role,
        verified: storage.currentUser.verified,
        profile: storage.currentUser.profile,
      },
    };
  }

  // Manuscript endpoints
  static async getManuscripts(params?: { status?: string; genre?: string; seekingBeta?: boolean }) {
    await delay();
    const storage = getStorage();
    let manuscripts = storage.manuscripts;

    if (params?.status) {
      manuscripts = manuscripts.filter(ms => ms.status === params.status);
    }
    if (params?.genre) {
      manuscripts = manuscripts.filter(ms => ms.genre === params.genre);
    }
    if (params?.seekingBeta) {
      manuscripts = manuscripts.filter(ms => ms.status === 'SEEKING_BETA_READERS');
    }

    // Populate author data
    const manuscriptsWithAuthors = manuscripts.map(ms => {
      const author = storage.users.find(u => u.id === ms.writerId);
      return {
        ...ms,
        author: formatUserForResponse(author),
        _count: {
          betaApplications: storage.betaApplications.filter(app => app.manuscriptId === ms.id && app.status === 'ACCEPTED').length,
        },
      };
    });

    return { manuscripts: manuscriptsWithAuthors };
  }

  static async getMyManuscripts() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const manuscripts = getManuscriptsByWriter(storage.currentUser.id);

    // Populate _count for beta applications
    const manuscriptsWithCounts = manuscripts.map(ms => ({
      ...ms,
      _count: {
        betaApplications: storage.betaApplications.filter(app => app.manuscriptId === ms.id && app.status === 'ACCEPTED').length,
      },
    }));

    return { manuscripts: manuscriptsWithCounts };
  }

  static async getManuscriptById(id: string) {
    await delay();
    const storage = getStorage();
    const manuscript = storage.manuscripts.find(ms => ms.id === id);

    if (!manuscript) {
      throw { status: 404, message: 'Manuscript not found' };
    }

    const writer = storage.users.find(u => u.id === manuscript.writerId);
    return {
      manuscript: {
        ...manuscript,
        writer: writer ? { name: writer.name, id: writer.id } : undefined,
      },
    };
  }

  static async createManuscript(data: Partial<MockManuscript>) {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const newManuscript: MockManuscript = {
      id: `ms-${Date.now()}`,
      writerId: storage.currentUser.id,
      title: data.title || '',
      genre: data.genre || '',
      wordCount: data.wordCount || 0,
      blurb: data.blurb || '',
      contentWarnings: data.contentWarnings,
      excerpt: data.excerpt,
      status: data.status || 'DRAFTING',
      targetAudience: data.targetAudience,
      completionStatus: data.completionStatus,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    storage.manuscripts.push(newManuscript);
    setStorage(storage);

    return { manuscript: newManuscript };
  }

  static async updateManuscript(id: string, data: Partial<MockManuscript>) {
    await delay();
    const storage = getStorage();
    const index = storage.manuscripts.findIndex(ms => ms.id === id);

    if (index === -1) {
      throw { status: 404, message: 'Manuscript not found' };
    }

    storage.manuscripts[index] = {
      ...storage.manuscripts[index],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    setStorage(storage);

    return { manuscript: storage.manuscripts[index] };
  }

  static async deleteManuscript(id: string) {
    await delay();
    const storage = getStorage();
    storage.manuscripts = storage.manuscripts.filter(ms => ms.id !== id);
    setStorage(storage);
    return { message: 'Manuscript deleted successfully' };
  }

  // Beta application endpoints
  static async submitApplication(data: Partial<MockBetaApplication>) {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const newApplication: MockBetaApplication = {
      id: `app-${Date.now()}`,
      manuscriptId: data.manuscriptId || '',
      readerId: storage.currentUser.id,
      status: 'PENDING',
      message: data.message || '',
      availability: data.availability || '',
      readingSpeed: data.readingSpeed || 'MODERATE',
      experienceLevel: data.experienceLevel || 'BEGINNER',
      strengths: data.strengths || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    storage.betaApplications.push(newApplication);
    setStorage(storage);

    return { application: newApplication };
  }

  static async getMyApplications() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const applications = getApplicationsByReader(storage.currentUser.id);

    // Attach manuscript and author info
    const applicationsWithManuscripts = applications.map(app => {
      const manuscript = storage.manuscripts.find(ms => ms.id === app.manuscriptId);
      const author = manuscript ? storage.users.find(u => u.id === manuscript.writerId) : undefined;
      return {
        ...app,
        manuscript: manuscript ? {
          id: manuscript.id,
          title: manuscript.title,
          genre: manuscript.genre,
          wordCount: manuscript.wordCount,
          author: formatUserForResponse(author),
        } : undefined,
      };
    });

    return { applications: applicationsWithManuscripts };
  }

  static async getMyActiveReads() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const applications = getApplicationsByReader(storage.currentUser.id)
      .filter(app => app.status === 'ACCEPTED');

    const assignments = applications.map(app => {
      const manuscript = storage.manuscripts.find(ms => ms.id === app.manuscriptId);
      const author = manuscript ? storage.users.find(u => u.id === manuscript.writerId) : undefined;

      // Calculate days remaining until deadline (using a mock 4-week deadline from accepted date)
      const assignedDate = new Date(app.updatedAt);
      const deadline = new Date(assignedDate);
      deadline.setDate(deadline.getDate() + 28); // 4 weeks from acceptance

      const now = new Date();
      const daysRemaining = Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

      // Mock progress (0-100)
      const daysSinceAssigned = Math.ceil((now.getTime() - assignedDate.getTime()) / (1000 * 60 * 60 * 24));
      const progress = Math.min(100, Math.floor((daysSinceAssigned / 28) * 100));

      return {
        id: app.id,
        manuscriptId: app.manuscriptId,
        title: manuscript?.title || 'Unknown',
        author: author?.name || 'Unknown Author',
        genre: manuscript?.genre || 'Unknown',
        wordCount: manuscript?.wordCount || 0,
        assignedDate: app.updatedAt,
        deadline: deadline.toISOString(),
        daysRemaining,
        progress,
        status: progress === 0 ? 'not_started' : progress >= 100 ? 'completed' : 'reading',
        feedbackSubmitted: false,
      };
    });

    return { assignments };
  }

  static async getApplicationsForManuscript(manuscriptId: string) {
    await delay();
    const storage = getStorage();
    const applications = getApplicationsByManuscript(manuscriptId);

    console.log('[MockAPI] Raw applications from storage:', applications);

    // Attach reader info
    const applicationsWithReaders = applications.map(app => {
      const reader = storage.users.find(u => u.id === app.readerId);
      return {
        ...app,
        reader: reader ? {
          ...formatUserForResponse(reader),
          profile: reader.profile,
        } : undefined,
      };
    });

    console.log('[MockAPI] Applications with readers:', applicationsWithReaders);

    return { applications: applicationsWithReaders };
  }

  static async getAllApplicationsForWriter() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    // Get all manuscripts for the current writer
    const manuscripts = getManuscriptsByWriter(storage.currentUser.id);
    const manuscriptIds = manuscripts.map(ms => ms.id);

    // Get all applications for these manuscripts
    const applications = storage.betaApplications.filter(app =>
      manuscriptIds.includes(app.manuscriptId)
    );

    // Attach reader and manuscript info
    const applicationsWithDetails = applications.map(app => {
      const reader = storage.users.find(u => u.id === app.readerId);
      const manuscript = storage.manuscripts.find(m => m.id === app.manuscriptId);
      return {
        ...app,
        reader: reader ? {
          ...formatUserForResponse(reader),
          profile: reader.profile,
          bio: reader.profile && 'bio' in reader.profile ? reader.profile.bio : '',
        } : undefined,
        manuscript: manuscript ? {
          id: manuscript.id,
          title: manuscript.title,
        } : undefined,
      };
    });

    return { applications: applicationsWithDetails };
  }

  static async updateApplicationStatus(id: string, status: string) {
    await delay();
    const storage = getStorage();
    const index = storage.betaApplications.findIndex(app => app.id === id);

    if (index === -1) {
      throw { status: 404, message: 'Application not found' };
    }

    storage.betaApplications[index] = {
      ...storage.betaApplications[index],
      status: status as any,
      updatedAt: new Date().toISOString(),
    };
    setStorage(storage);

    return { application: storage.betaApplications[index] };
  }

  static async getActiveReadersForManuscript(manuscriptId: string) {
    await delay();
    const storage = getStorage();
    const readers = getApprovedReaders(manuscriptId);
    return { readers };
  }

  // Query endpoints
  static async submitQuery(data: Partial<MockQuery>) {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const newQuery: MockQuery = {
      id: `query-${Date.now()}`,
      writerId: storage.currentUser.id,
      agentId: data.agentId || '',
      manuscriptTitle: data.manuscriptTitle || '',
      genre: data.genre || '',
      wordCount: data.wordCount || 0,
      queryLetter: data.queryLetter || '',
      excerpt: data.excerpt,
      status: 'SUBMITTED',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    storage.queries.push(newQuery);
    setStorage(storage);

    return { query: newQuery };
  }

  static async getMyQueries() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const queries = getQueriesByWriter(storage.currentUser.id);

    // Attach agent and manuscript info
    const queriesWithDetails = queries.map(query => {
      const agent = storage.users.find(u => u.id === query.agentId);
      const manuscript = storage.manuscripts.find(m => m.id === query.manuscriptId);
      return {
        ...query,
        agent: agent ? {
          ...formatUserForResponse(agent),
          profile: agent.profile,
        } : undefined,
        manuscript: manuscript ? {
          id: manuscript.id,
          title: manuscript.title,
        } : undefined,
      };
    });

    return { queries: queriesWithDetails };
  }

  static async getQueryInbox() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const queries = getQueriesByAgent(storage.currentUser.id);

    // Attach writer info
    const queriesWithWriters = queries.map(query => {
      const writer = storage.users.find(u => u.id === query.writerId);
      return {
        ...query,
        writer: writer ? {
          id: writer.id,
          name: writer.name,
        } : undefined,
      };
    });

    return { queries: queriesWithWriters };
  }

  static async getQueryById(id: string) {
    await delay();
    const storage = getStorage();
    const query = storage.queries.find(q => q.id === id);

    if (!query) {
      throw { status: 404, message: 'Query not found' };
    }

    const writer = storage.users.find(u => u.id === query.writerId);
    const agent = storage.users.find(u => u.id === query.agentId);

    return {
      query: {
        ...query,
        writer: writer ? { id: writer.id, name: writer.name } : undefined,
        agent: agent ? { id: agent.id, name: agent.name, profile: agent.profile } : undefined,
      },
    };
  }

  static async updateQueryStatus(id: string, status: string, response?: string) {
    await delay();
    const storage = getStorage();
    const index = storage.queries.findIndex(q => q.id === id);

    if (index === -1) {
      throw { status: 404, message: 'Query not found' };
    }

    storage.queries[index] = {
      ...storage.queries[index],
      status: status as any,
      agentResponse: response || storage.queries[index].agentResponse,
      updatedAt: new Date().toISOString(),
    };
    setStorage(storage);

    return { query: storage.queries[index] };
  }

  // Feedback endpoints
  static async submitFeedback(data: Partial<MockFeedback>) {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const newFeedback: MockFeedback = {
      id: `feedback-${Date.now()}`,
      manuscriptId: data.manuscriptId || '',
      readerId: storage.currentUser.id,
      overallImpression: data.overallImpression || '',
      strengths: data.strengths || '',
      weaknesses: data.weaknesses || '',
      suggestions: data.suggestions || '',
      rating: data.rating || 3,
      createdAt: new Date().toISOString(),
    };

    storage.feedback.push(newFeedback);
    setStorage(storage);

    return { feedback: newFeedback };
  }

  static async getFeedbackForManuscript(manuscriptId: string) {
    await delay();
    const storage = getStorage();
    const feedback = getFeedbackByManuscript(manuscriptId);

    // Attach reader info
    const feedbackWithReaders = feedback.map(fb => {
      const reader = storage.users.find(u => u.id === fb.readerId);
      return {
        ...fb,
        reader: reader ? {
          id: reader.id,
          name: reader.name,
        } : undefined,
      };
    });

    return { feedback: feedbackWithReaders };
  }

  // Agent endpoints
  static async getAgents(params?: { acceptingQueries?: boolean }) {
    await delay();
    const storage = getStorage();
    let agents = storage.users.filter(u => u.role === 'AGENT');

    if (params?.acceptingQueries) {
      agents = agents.filter(agent =>
        agent.profile && 'acceptingQueries' in agent.profile && agent.profile.acceptingQueries
      );
    }

    // Format agents with displayName and profile data
    const formattedAgents = agents.map(agent => ({
      ...formatUserForResponse(agent),
      profile: agent.profile,
      // Extract common profile fields for easier access
      preferredGenres: agent.profile && 'preferredGenres' in agent.profile ? agent.profile.preferredGenres : [],
      acceptingQueries: agent.profile && 'acceptingQueries' in agent.profile ? agent.profile.acceptingQueries : false,
      bio: agent.profile && 'bio' in agent.profile ? agent.profile.bio : '',
      agencyName: agent.profile && 'agencyName' in agent.profile ? agent.profile.agencyName : '',
      manuscriptWishlist: agent.profile && 'manuscriptWishlist' in agent.profile ? agent.profile.manuscriptWishlist : [],
      responseTime: agent.profile && 'responseTime' in agent.profile ? agent.profile.responseTime : '',
    }));

    return { agents: formattedAgents };
  }

  static async getAgentById(id: string) {
    await delay();
    const storage = getStorage();
    const agent = storage.users.find(u => u.id === id && u.role === 'AGENT');

    if (!agent) {
      throw { status: 404, message: 'Agent not found' };
    }

    return { agent };
  }

  // Reader endpoints
  static async getReaderById(id: string) {
    await delay();
    const storage = getStorage();
    const reader = storage.users.find(u => u.id === id && u.role === 'READER');

    if (!reader) {
      throw { status: 404, message: 'Reader not found' };
    }

    return { reader };
  }

  // User/Profile endpoints
  static async getProfile() {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    return {
      user: storage.currentUser,
    };
  }

  static async updateProfile(data: any) {
    await delay();
    const storage = getStorage();

    if (!storage.currentUser) {
      throw { status: 401, message: 'Not authenticated' };
    }

    const userIndex = storage.users.findIndex(u => u.id === storage.currentUser!.id);
    if (userIndex !== -1) {
      storage.users[userIndex] = {
        ...storage.users[userIndex],
        ...data,
        profile: {
          ...storage.users[userIndex].profile,
          ...data.profile,
        },
      };
      storage.currentUser = storage.users[userIndex];
      setStorage(storage);
    }

    return { user: storage.currentUser };
  }

  // Demo-specific: Switch user account
  static async switchUser(userId: string) {
    await delay();
    const storage = getStorage();
    const user = storage.users.find(u => u.id === userId);

    if (!user) {
      throw { status: 404, message: 'User not found' };
    }

    storage.currentUser = user;
    setStorage(storage);

    const token = `demo-token-${user.id}`;
    localStorage.setItem('token', token);

    const userData = {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      verified: user.verified,
    };

    // Store user data in localStorage so Header can read it
    localStorage.setItem('user', JSON.stringify(userData));

    return {
      token,
      user: userData,
    };
  }

  // Reset demo data to initial state
  static resetDemoData() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('token');
    initializeStorage();
  }
}

// Initialize storage on module load
initializeStorage();

export default MockAPI;
